// Lofi piano (docs/AUDIO_DESIGN.md §12): a sleepy felt piano on warm, simple chords (maj7, add9, m7, 6/9, sus, the
// borrowed iv), broken chords and slow arpeggios under the sustain pedal, the piano's own left hand for a bass, a sparse
// right-hand melody, soft brushes (nearly none when chill), a little more tape wow than jazzhop.
// The left hand's root is the voicing's lowest note (an `ep` event), so 'keys only' is a solo piano with both hands;
// the `bass` events carry the rest of the left hand (fifths, octaves, tenths, re-strikes on the kick).
import { QUALITIES } from '../harmony.js';
import { createPiano, createBrushKit } from './piano-instruments.js';

const mod12 = n => ((n % 12) + 12) % 12;
const pickW = (r, w) => {
  let u = r() * Object.values(w).reduce((a, b) => a + b, 0);
  for (const [k, v] of Object.entries(w)) if ((u -= v) < 0) return k;
  return Object.keys(w)[0];
};

/* ---------------- voicing: left-hand root + an open right hand ---------------- */
// Right-hand tones per quality (semitones over the root; the root itself is the left hand's): 3 5 7 9 on sevenths (the 9th
// added for colour), 3 5 1 9 on add9 (the root–9th whole step), 3 5 6 9 on sixths, 4 5 ♭7 9 on sus chords, R 3 5 ♭7 on
// plain dominants (no 9th: V7 in minor), 3 ♭5 ♭7 11 on m7♭5. Unknown qualities: their tones minus the root, 5th dropped first.
const RH = {
  maj7: [4, 7, 11, 14], maj9: [4, 7, 11, 14], m7: [3, 7, 10, 14], m9: [3, 7, 10, 14], add9: [4, 7, 12, 14], madd9: [3, 7, 12, 14],
  6: [4, 7, 9, 14], 69: [4, 7, 9, 14], m6: [3, 7, 9, 14], '9sus4': [5, 7, 10, 14], '7sus4': [5, 7, 10, 14], 7: [4, 7, 10, 12],
  9: [4, 7, 10, 14], 13: [4, 10, 14, 21], m7b5: [3, 6, 10, 17], '7b9': [4, 7, 10, 13],
};
const rhTones = q => {
  if (RH[q]) return RH[q];
  let iv = [...new Set((QUALITIES[q] || QUALITIES.maj7).filter(i => i % 12))];
  if (iv.length > 4) iv = iv.filter(i => i !== 7).slice(0, 4);
  return iv.length ? iv : [4, 7];
};
const perms = n => (n <= 1 ? [[0]] : perms(n - 1).flatMap(p => Array.from({ length: n }, (_, k) => [...p.slice(0, k), n - 1, ...p.slice(k)])));
const PERMS = [null, perms(1), perms(2), perms(3), perms(4)];
const avg = a => a.reduce((s, x) => s + x, 0) / a.length;
/** Right-hand candidates: every order of the tones stacked upwards from a lowest note in MIDI 48-61, kept when the top is
 *  62-76 and the span 9-22 (open, pianistic), with no minor 2nd between neighbours, no minor 9th anywhere and nothing
 *  closer than a minor 3rd below MIDI 55. */
function rhCandidates(pcs) {
  const out = [];
  for (const order of PERMS[pcs.length]) for (let lo = 48; lo <= 61; lo++) {
    if (mod12(lo) !== pcs[order[0]]) continue;
    const v = [lo];
    for (let k = 1; k < order.length; k++) { let m = v[k - 1] + 1; while (mod12(m) !== pcs[order[k]]) m++; v.push(m); }
    const top = v[v.length - 1], span = top - lo;
    if (top < 62 || top > 76 || span < 9 || span > 22) continue;
    let ok = true;
    for (let k = 1; k < v.length && ok; k++) if (v[k] - v[k - 1] < 2 || (v[k - 1] < 55 && v[k] - v[k - 1] < 3)) ok = false;
    for (let a = 0; a < v.length && ok; a++) for (let c = a + 1; c < v.length; c++) if (v[c] - v[a] === 13) ok = false;
    if (ok) out.push(v);
  }
  return out;
}
/** Voice-leading cost against the previous right hand (voices paired from the top): Σ|Δ| + 1.5·max(0, |Δtop| − 2)
 *  (+4 past a 4th) − .5 per common tone, + .35·|mean − 63| + .6·max(0, top − 74) + .15·|span − 15| + 2 per semitone the
 *  right hand sits closer than a 5th to the left hand. The first chord: the register terms and .3·|top − 70|, with the
 *  mean and top targets moved by the track's register (plan.register). */
function rhCost(v, prev, lh, register = 0) {
  const top = v[v.length - 1], r = prev ? 0 : register;
  let c = 0.35 * Math.abs(avg(v) - 63 - r) + 0.6 * Math.max(0, top - 74) + 0.15 * Math.abs(top - v[0] - 15) + 2 * Math.max(0, lh + 7 - v[0]);
  if (!prev) return c + 0.3 * Math.abs(top - 70 - r);
  for (let i = 1; i <= Math.min(v.length, prev.length); i++) c += Math.abs(v[v.length - i] - prev[prev.length - i]);
  const dt = Math.abs(top - prev[prev.length - 1]);
  c += 1.5 * Math.max(0, dt - 2) + (dt > 5 ? 4 : 0);
  for (const m of v) if (prev.includes(m)) c -= 0.5;
  return c;
}
/** the left hand's root: MIDI 34-50, nearest the previous one, leaning to 41 */
const lhRoot = (pc, prev = 41) => {
  let best = 34 + mod12(pc - 34), bc = Infinity;
  for (let m = best; m <= 50; m += 12) { const c = Math.abs(m - prev) + 0.4 * Math.abs(m - 41); if (c < bc) { bc = c; best = m; } }
  return best;
};
/** [left-hand root, ...right hand low → high]; the same chord again keeps its voicing (re-strikes and 2-bar chords). */
function voicePiano(sp, prev, chord, plan) {
  const root = mod12(plan.key.tonic + chord.root), pcs = rhTones(chord.q).map(i => mod12(root + i));
  const set = new Set(pcs);
  if (prev && mod12(prev[0]) === root && prev.length === pcs.length + 1 && prev.slice(1).every(m => set.has(mod12(m)))
    && pcs.every(pc => prev.slice(1).some(m => mod12(m) === pc))) return prev.slice();
  const lh = lhRoot(root, prev ? prev[0] : 41), pr = prev ? prev.slice(1) : null;
  let best = null, bc = Infinity;
  for (const v of rhCandidates(pcs)) { const c = rhCost(v, pr, lh, plan.register); if (c < bc) { bc = c; best = v; } }
  if (!best) { best = []; for (const pc of pcs) { let m = 55 + mod12(pc - 55); while (best.length && m <= best[best.length - 1]) m += 12; best.push(m); } }
  return [lh, ...best];
}

/* ---------------- comping: cells of [step, length, velocity, pick] ---------------- */
// Every note is held to the end of the bar, so the engine's cut at the chord's end is the sustain pedal (the notes ring
// until the harmony changes). Every cell strikes at steps 0 and 8, so the 2nd chord of a split bar always has its root.
// Voicing indices: 0 = the left hand's root, 1 = the right hand's lowest, −1 = the top.
const cell = (s, v = 1, pick = null) => (pick ? [s, 16 - s, v, pick] : [s, 16 - s, v]);
const RHAND = [1, 2, -2, -1];
const COMPS = {
  hold: [cell(0)],                                                                                          // a held block chord
  pulse: [cell(0), cell(8, 0.8)],                                                                           // block on 1 and 3
  broken: [cell(0, 1, [0]), cell(2, 0.7, [1]), cell(4, 0.75, [2]), cell(6, 0.8, [-2, -1]), cell(8, 0.8, [0]), cell(10, 0.68, [1, 2]), cell(12, 0.72, [-1])],
  roll: [cell(0, 1, [0]), cell(1, 0.8, [1]), cell(2, 0.85, [2]), cell(3, 0.9, [-2, -1]), cell(8, 0.75, [0]), cell(10, 0.6, [-1])],   // a 16th roll up
  lilt: [cell(0, 1, [0]), cell(6, 0.85, RHAND), cell(8, 0.8, [0]), cell(12, 0.75, RHAND)],                 // root, right hand on the "and"
  arp8: [cell(0, 1, [0]), cell(2, 0.7, [1]), cell(4, 0.75, [2]), cell(6, 0.75, [-1]), cell(8, 0.8, [0]), cell(10, 0.7, [-2]), cell(12, 0.72, [-1]), cell(14, 0.65, [2])],
  sparse: [cell(0), cell(8, 0.7, [0]), cell(10, 0.6, [-1])],                                                // block, root again, a top-note answer
};

/* ---------------- drums: soft brushes ---------------- */
// K kick · S brush snare · H brush tap (o = a swish) · R cross-stick · X brush sweep; s = soft (×.55), g = ghost
const GRIDS = {
  N1: { name: 'hush', K: 'x...............', R: '........r.......', X: '....s.......s...' },
  N2: { name: 'half', K: 'x.....x.........', S: '........x.......', H: 's.s.s.s.s.s.s.s.' },
  N3: { name: 'side', K: 'x.........x.....', R: '....r.......r...', X: 's.s.s.s.s.s.s.s.' },
  N4: { name: 'brush', K: 'x......x..x.....', S: '....x.......x...', X: 'x.s.x.s.x.s.x.s.' },
  N5: { name: 'nod', K: 'x.....x...x.....', S: '....x..g....x...', H: 'x.s.x.s.x.s.x.so' },
  break: { R: '........r.......', X: 's...s...s...s...' },
};
const GROOVES = {
  chill: { N1: 2.4, N2: 2.4, N3: 1, N4: 0.3, N5: 0 },
  slow: { N1: 0.6, N2: 2, N3: 1.4, N4: 0.8, N5: 0.3 },
  fast: { N1: 0.2, N2: 1, N3: 1.4, N4: 1.2, N5: 0.8 },
  upbeat: { N1: 0, N2: 0.8, N3: 1, N4: 1.4, N5: 1.4 },
};
// left-hand patterns over the chord's root: a broken fifth (and octave), the octave (below when the root is high), a
// tenth, or the root alone
const LH = { fifth: 1, octave: 0.8, tenth: 0.6, still: 0.3 };

export default {
  id: 'piano', name: 'Piano', desc: 'Felt piano, simple warm chords, soft or no drums',
  //            chill                        balanced                     upbeat
  vary: 'jazz',
  energy: {
    stretch:    [0.55,                       0.4,                         0.25],
    bpm:        [[58, 68],                   [64, 78],                    [72, 86]],
    swing:      [[53, 58],                   [52, 57],                    [51, 55]],
    snareLag:   [[0.01, 0.02],               [0.008, 0.016],              [0.004, 0.012]],
    groove:     [[GROOVES.chill, GROOVES.chill], [GROOVES.slow, GROOVES.fast], [GROOVES.upbeat, GROOVES.upbeat]],
    drumLevel:  [0.66,                       0.72,                        0.8],
    kitLp:      [[3200, 4600],               [3800, 5600],                [4400, 6600]],
    epLp:       [[1900, 2800],               [2200, 3400],                [2600, 4000]],     // the felt low-pass on the piano bus
    strum:      [[0.018, 0.04],              [0.012, 0.03],               [0.008, 0.022]],
    epVel:      [[0.45, 0.55],               [0.46, 0.56],                [0.5, 0.6]],
    comp: [
      { hold: [0.8, 1.4], pulse: [0.1, 0.4], broken: [0.4, 1], roll: [0.3, 0.8], lilt: [0, 0.2], arp8: [0, 0.1], sparse: [0.3, 0.8] },
      { hold: [0.3, 0.9], pulse: [0.2, 0.6], broken: [0.4, 1], roll: [0.2, 0.6], lilt: [0.1, 0.5], arp8: [0.05, 0.3], sparse: [0.2, 0.6] },
      { hold: [0.1, 0.4], pulse: [0.3, 0.8], broken: [0.3, 0.8], roll: [0.1, 0.4], lilt: [0.3, 0.9], arp8: [0.2, 0.6], sparse: [0.1, 0.4] },
    ],
    push:       [0.03,                       0.08,                        0.15],
    lead:       [0.45,                       0.6,                         0.7],
    tone:       [[3800, 5800],               [4600, 7200],                [5400, 8200]],
    wowCents:   [[8, 14],                    [6, 12],                     [5, 10]],
    dust:       [[1, 1.7],                   [0.8, 1.5],                  [0.6, 1.2]],
    introHats:  [0.1,                        0.25,                        0.45],
    hatsFirst:  [0.4,                        0.3,                         0.2],
    breakDrums: [0.15,                       0.35,                        0.6],
    breakLead:  [0.25,                       0.35,                        0.45],
    roll:       [[0.2, 0.4],                 [0.12, 0.3],                 [0.06, 0.2]],      // odds a block chord is rolled
    restrike:   [[0.1, 0.25],                [0.2, 0.4],                  [0.3, 0.55]],      // odds per kick of a left-hand re-strike
    // development (arrange.js BY_ENERGY): the piano alone, or the right hand first
    intro:      [{ keys: 0.45, beat: 0.1, groove: 0.05, lead: 0.3, cold: 0.1 }, { keys: 0.45, beat: 0.1, groove: 0.05, lead: 0.3, cold: 0.1 }, { keys: 0.45, beat: 0.1, groove: 0.05, lead: 0.3, cold: 0.1 }],
  },
  progressions: {
    major: {
      p1: [[0, 'maj7', 4], [9, 'm9', 4], [5, 'maj7', 4], [7, '9sus4', 4]],                   // I–vi–IV–V
      p2: [[5, 'maj7', 4], [0, 'add9', 4], [9, 'm7', 4], [7, '9sus4', 4]],                   // IV–I–vi–V
      p3: [[9, 'm7', 4], [5, 'maj7', 4], [0, 'add9', 4], [7, 'add9', 4]],                    // vi–IV–I–V
      p4: [[0, 'maj7', 4], [4, 'm7', 4], [5, 'maj7', 4], [5, 'm6', 4]],                      // Imaj7–iii7–IVmaj7–ivm6
      p5: [[5, 'maj9', 4], [5, 'm6', 4], [0, 'maj7', 4], [0, '69', 4]],                      // IV–iv–I–I
      p6: [[0, 'add9', 4], [7, 'add9', 2], [9, 'm7', 2], [5, 'maj7', 4], [5, 'm6', 4]],     // I–V|vi–IV–iv
      p7: [[2, 'm9', 4], [7, '9sus4', 4], [0, 'maj7', 4], [9, 'm7', 4]],                     // ii–V–I–vi
      p8: [[5, 'maj7', 4], [4, 'm7', 4], [2, 'm7', 4], [0, 'add9', 4]],                      // IV–iii–ii–I
    },
    minor: {
      q1: [[0, 'm9', 4], [8, 'maj7', 4], [3, 'maj7', 4], [10, 'add9', 4]],                   // i–VI–III–VII
      q2: [[0, 'madd9', 4], [5, 'm7', 4], [8, 'maj7', 4], [7, '7sus4', 2], [7, '7', 2]],     // i–iv–VI–V
      q3: [[8, 'maj7', 4], [10, 'add9', 4], [0, 'm9', 4], [0, 'm7', 4]],                     // VI–VII–i–i
      q4: [[0, 'm9', 4], [10, 'add9', 4], [8, 'maj9', 4], [10, '6', 4]],                     // i–VII–VI–VII
      q5: [[0, 'm9', 4], [5, 'm7', 4], [10, 'add9', 4], [3, 'maj7', 4]],                     // i–iv–VII–III
      q6: [[5, 'm9', 4], [0, 'm7', 4], [8, 'maj7', 4], [7, '7sus4', 4]],                     // iv–i–VI–V
      q7: [[0, 'madd9', 4], [3, 'maj7', 4], [10, '6', 4], [5, 'add9', 4]],                   // i–III–VII–IV (Dorian)
    },
  },
  turnarounds: { major: [[5, 'maj7', 2], [5, 'm6', 2]], minor: [[8, 'maj7', 2], [7, '7sus4', 2]] },   // IV–iv · VI–V
  grids: GRIDS,
  fills: {
    R1: { roll: [[12, 0.35], [13, 0.25], [14, 0.4], [15, 0.55]] },                          // a brush roll
    R2: { stop: 12 },                                                                         // the band stops, the piano rings
    R3: { kicks: [14] },
    R4: { open: 14 },                                                                         // a swish
  },
  comps: COMPS,
  keysLevel: 1.35,

  plan(plan, { r, E }) {
    const rr = (a, b, d = 2) => +(a + r() * (b - a)).toFixed(d);
    plan.piano = {
      detune: rr(0.5, 1.5), stretch: rr(0.7, 1.3), hammer: rr(0.7, 1.3), bright: rr(0.85, 1.15), roll: rr(...E.roll),
      lh: pickW(r, LH), restrike: rr(...E.restrike),
    };
    const k = plan.drums.kit;
    plan.drums.kit = {
      kickF0: rr(105, 125, 1), kickF1: rr(44, 52, 1), kickDecay: rr(0.16, 0.24, 3), snareDecay: rr(0.1, 0.16, 3),
      snareBP: rr(1700, 2400, 0), hatHP: rr(4200, 5600, 0), hatClosed: rr(0.02, 0.035, 3), lp: k.lp,
    };
    if (plan.lead) plan.lead.timbre = 'piano';
  },

  voicing: voicePiano,

  /** The rest of the left hand under the voicing's root: a broken fifth (+ octave), the octave (below the root from MIDI 43,
   *  else above), or a tenth at each chord (the fifth when it would touch the right hand), plus a few re-strikes on the kick
   *  (A/B only); all held to the chord's end (the pedal), cut by a fill's stop or a push. */
  bass({ plan, st, i, b, mode, chords, kicks, stop }) {
    const P = plan.piano, rS = st.rS, v = st.prevVoicing, out = [];
    const end = Math.min(stop ?? 16, st.pushed === i + 1 ? 14 : 16);
    const lh = rS() < 0.2 ? pickW(rS, LH) : P.lh, soft = mode === 'kick' ? 1 : 0.8;
    for (const c of chords) {
      const c0 = c.start * 4, c1 = c.final ? (b.n - 2) * 16 : Math.min(c0 + c.beats * 4, end);
      if (c0 >= c1) continue;
      const pc = mod12(plan.key.tonic + c.root), iv = QUALITIES[c.q] || [0, 4, 7];
      const root = v && mod12(v[0]) === pc ? v[0] : lhRoot(pc, v ? v[0] : 41), rhLo = v ? v[1] : 55;
      const fifth = root + (iv.includes(7) ? 7 : iv.includes(6) ? 6 : 7), third = iv.includes(4) ? 4 : iv.includes(3) ? 3 : null;
      const fits = m => m < rhLo - 1, add = (s, m, vel) => { if (s < c1 && fits(m)) out.push({ s, midi: m, vel, dur: c1 - s }); };
      const len = c1 - c0, style = c.final ? 'fifth' : lh, tenth = third != null ? root + 12 + third : null, brk = len >= 4 ? 2 : 0;
      if (style === 'fifth') { add(c0 + brk, fifth, 0.62 * soft); if (len >= 8) add(c0 + 4, root + 12, 0.56 * soft); }
      else if (style === 'octave') {
        if (root >= 43) add(c0, root - 12, 0.62 * soft); else if (fits(root + 12)) add(c0, root + 12, 0.6 * soft); else add(c0 + brk, fifth, 0.6 * soft);
      } else if (style === 'tenth') add(c0 + (len >= 4 ? 1 : 0), tenth != null && tenth < rhLo - 2 ? tenth : fifth, 0.6 * soft);
      if (mode === 'kick') for (const s of kicks) if (s > c0 + 3 && s < c1 - 1 && rS() < P.restrike) add(s, rS() < 0.6 ? root : root + 12, 0.5 + 0.1 * rS());
      st.prevBass = root;
    }
    return out.sort((a, c) => a.s - c.s);
  },

  voices(ac, dry, wet, plan, ctx) {
    const piano = createPiano(ac, dry, wet, plan, ctx);
    return { ep: piano, bass: piano.left, lead: piano.right, kit: createBrushKit(ac, dry, wet, plan.drums.kit, ctx) };
  },
};
